<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pt-3 tw-pb-3.5">
        <base-breadcrumbs :breadcrumbs="breadcrumbs" />
      </base-content>
      <base-content class="tw-flex tw-justify-between tw-items-center tw-pt-0">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Vendor Price List</h1>
        <base-button @click="downloadPdf()" :disabled="isDownloading || isLoading">
          <template v-slot:icon>
            <download-icon v-if="!isDownloading" />
          </template>
          {{ isDownloading ? "Downloading..." : "Download" }}
        </base-button>
      </base-content>
    </div>
    <base-content>
      <div id="vendor-price-list-pdf-content" class="tw-bg-white tw-p-4 sm:tw-p-6 md:tw-p-8" style="box-sizing: border-box; font-family: sans-serif; color: #000;">
        <div v-if="!isLoading" class="tw-mb-6">
          <div class="tw-px-4" :class="{ 'tw-bg-primary-500': logo === 'rx-harmony-logo' }" style="width: 10rem; height: 4rem;">
            <component :is="logo" class="tw-w-28 tw-h-16" />
          </div>
          <p class="tw-font-semibold tw-mt-1">{{ todayDateFormatted }}</p>
        </div>

        <div v-if="isLoading" class="tw-flex tw-justify-center tw-items-center tw-min-h-[200px] tw-text-gray-600 tw-text-lg">
          Loading data...
        </div>
        <div v-else>
          <div class="tw-flex tw-space-x-20 tw-item-center tw-mb-6 tw-text-sm">
            <div>
              <p class="tw-font-semibold tw-text-base tw-mb-1">Practice Name</p>
              <p class="tw-font-semibold tw-text-base tw-mb-1">Address 1</p>
              <p class="tw-font-semibold tw-text-base tw-mb-1">Address 2</p>
              <p class="tw-font-semibold tw-text-base tw-mb-1">City, State Zip</p>
              <p class="tw-font-semibold tw-text-base tw-mb-1">Email</p>
              <p class="tw-font-semibold tw-text-base tw-mb-1">Phone</p>
            </div>
            <div v-if="currentLocation">
              <p class="tw-font-normal tw-text-base tw-mb-1">{{ currentLocation.name || "-" }}</p>
              <p class="tw-font-normal tw-text-base tw-mb-1">{{ currentLocation.line1 || "-" }}</p>
              <p class="tw-font-normal tw-text-base tw-mb-1">{{ currentLocation.line2 || "-" }}</p>
              <p class="tw-font-normal tw-text-base tw-mb-1">{{ currentLocation.city || "-" }}, {{ currentLocation.state || "-" }} {{ currentLocation.zip || "-" }}</p>
              <p class="tw-font-normal tw-text-base tw-mb-1">{{ currentLocation.emailAddress || "-" }}</p>
              <p class="tw-font-normal tw-text-base tw-mb-1">{{ currentLocation.phoneNumber || "-" }}</p>
            </div>
          </div>
          <h2 class="tw-text-xl tw-font-semibold tw-mb-4">Exhibit B Fee Schedule</h2>

          <div class="tw-overflow-x-auto">
            <table class="tw-border-collapse">
              <thead>
                <tr>
                  <th class="tw-py-2 tw-px-4 tw-text-left tw-font-semibold tw-text-sm" style="width: 400px;"></th>
                  <th class=" tw-text-center tw-border tw-border-black" style="background-color: #dff0d8; color: #000; border: 0.5px solid gray; width:140px">
                    Practice Cost
                  </th>
                  <th
                    v-for="groupName in uniqueProgramGroups"
                    :key="groupName"
                    class="tw-py-2 tw-px-4 tw-text-center tw-border tw-border-black tw-font-bold tw-align-top tw-text-sm"
                    style="background-color: #1f4c1c; color: white; padding-left: 12px; border: 0.5px solid gray;width: 120px;"
                  >
                    {{ groupName }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="uiRow in orderedUiRows" :key="uiRow.name">
                  <td class="tw-py-2 tw-px-4 tw-text-xs sm:tw-text-base tw-font-normal">{{ uiRow.name }}</td>
                  <td
                    class="tw-py-2 tw-px-5 tw-text-center tw-border tw-border-black tw-align-top tw-text-xs sm:tw-text-base"
                    style="background-color: #dff0d8; color: #000;  padding: 0.5rem 1.25rem; text-align: center; border: 0.5px solid gray;"
                  >
                    <div class="tw-flex tw-justify-between tw-w-full">
                      <span>$</span>
                      <span>{{ uiRow.providerPriceDisplay !== 0 ? uiRow.providerPriceDisplay : "-" }}</span>
                    </div>
                  </td>
                  <td
                    v-for="groupName in uniqueProgramGroups"
                    :key="groupName"
                    class="tw-py-2 tw-px-4 tw-border tw-border-black tw-align-top tw-text-xs sm:tw-text-base"
                    style="border: 0.5px solid gray;"
                    :class="{
                      'tw-bg-gray-200':
                        uiRow.pricesByFormattedGroup[groupName] === null ||
                        uiRow.pricesByFormattedGroup[groupName] === undefined ||
                        uiRow.pricesByFormattedGroup[groupName] === undefined ||
                        uiRow.pricesByFormattedGroup[groupName] === '',
                    }"
                  >
                    <div class="tw-flex tw-justify-between tw-w-full">
                      <span v-if="uiRow.pricesByFormattedGroup[groupName] !== null && uiRow.pricesByFormattedGroup[groupName] !== undefined">$</span>
                      <span>
                        {{ uiRow.pricesByFormattedGroup[groupName] === 0 ? "-" : uiRow.pricesByFormattedGroup[groupName] || "" }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </base-content>
  </div>
</template>

<script>
import PredictileLogo from "@/assets/images/predictile-logo-square.svg";
import RxHarmonyLogo from "@/assets/images/rx-harmony-logo.svg";
import { DownloadIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import { orderBy, uniq } from "lodash";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default {
  name: "VendorPriceListPdf",
  components: {
    DownloadIcon,
    PredictileLogo,
    RxHarmonyLogo,
  },
  props: {
    businessId: String,
    businessLocationId: String,
  },
  data() {
    return {
      isLoadingPrograms: false,
      isLoadingMappings: false,
      isSaving: false,
      isApplyToAllClicked: false,
      businessLocation: {},
      programsData: [],
      uiRows: [],
      programPricesMap: {},
      applyToAllLocations: false,
      isDownloading: false,
    };
  },
  computed: {
    ...mapGetters("organization", ["cobrandId"]),
    ...mapGetters("location", ["locations"]),

    breadcrumbs() {
      return [
        { title: "Settings", onClick: () => this.$router.push(`/businesses/${this.businessId}/business`) },
        { title: "Locations", onClick: () => this.$router.push(`/businesses/${this.businessId}/business/locations`) },
        { title: "Vendor Price List" },
      ];
    },
    logo() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return "rx-harmony-logo";
      }
      return "predictile-logo";
    },
    currentLocation() {
      return this.locations.find((loc) => loc.businessLocationId === this.businessLocationId);
    },
    uniqueProgramGroups() {
      const allGroups = this.programsData.map((program) => program.programGroup).filter(Boolean);

      const formattedGroups = allGroups.map((group) => this.formatProgramGroupName(group));

      return orderBy(uniq(formattedGroups), [], ["desc"]);
    },
    orderedUiRows() {
      return orderBy(this.uiRows, ["name"], ["asc"]);
    },
    isLoading() {
      return this.isLoadingPrograms || this.isLoadingMappings;
    },
    todayDateFormatted() {
      const today = new Date();
      const options = { year: "numeric", month: "long", day: "numeric" };
      return today.toLocaleDateString("en-US", options);
    },
  },
  created() {
    this.fetchAndPrepareProgramData();
  },
  methods: {
    formatCentsToDisplay(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents) || cents === null || cents === undefined) {
        return 0;
      }
      return Math.round(cents / 100);
    },

    async fetchAndPrepareProgramData() {
      try {
        await this.getPrograms();
        await this.getPricesMappings();
        this.filterAndApplyMappedProgramPrices();
        this.buildUiRowsFromProgramsData();
      } catch (error) {
        console.error("Error fetching or preparing program data:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program data",
          message: "There was an issue fetching program pricing. Please try again.",
          error,
        });
      }
    },

    async getPrograms() {
      try {
        this.isLoadingPrograms = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);

        this.programsData = response.data.map((program) => ({
          ...program,
          businessPlanPriceCents: 0,
          businessPlanPriceDisplay: 0,
          providerPriceCents: 0,
          providerPriceDisplay: 0,
          priceFloorCents: 0,
          priceFloorDisplay: 0,
          ceilingPriceCents: 0,
          ceilingPriceDisplay: 0,
        }));
      } catch (error) {
        console.error("Error fetching master programs:", error);
        throw error;
      } finally {
        this.isLoadingPrograms = false;
      }
    },

    async getPricesMappings() {
      try {
        this.isLoadingMappings = true;
        const url = `/businesses/${this.businessId}/locations/${this.businessLocationId}/mappings/program-prices`;
        const response = await this.$axios.get(url);

        this.programPricesMap = {};

        if (Array.isArray(response.data)) {
          response.data.forEach((mapping) => {
            const programId = mapping.businessProgramId;

            if (!programId) {
              return;
            }
            const businessPlanPrice = mapping.businessPlanPriceCents || 0;
            const providerPrice = mapping.providerPriceCents || 0;
            const priceFloor = mapping.priceFloorCents || 0;
            const ceilingPrice = mapping.ceilingPriceCents || 0;

            this.programPricesMap[programId] = {
              businessPlanPriceCents: businessPlanPrice,
              providerPriceCents: providerPrice,
              priceFloorCents: priceFloor,
              ceilingPriceCents: ceilingPrice,
            };
          });
        }
      } catch (error) {
        console.error("Error fetching program price mappings:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program price mappings",
          message: "There was an issue fetching program pricing. Please try again.",
          error,
        });
        throw error;
      } finally {
        this.isLoadingMappings = false;
      }
    },

    filterAndApplyMappedProgramPrices() {
      // Only keep programs that have a saved mapping
      this.programsData = this.programsData.filter((program) => {
        const savedPrices = this.programPricesMap[program.businessProgramId];
        if (!savedPrices) return false;

        // Apply mapped prices
        program.businessPlanPriceCents = savedPrices.businessPlanPriceCents;
        program.businessPlanPriceDisplay = this.formatCentsToDisplay(savedPrices.businessPlanPriceCents);
        program.providerPriceCents = savedPrices.providerPriceCents;
        program.providerPriceDisplay = this.formatCentsToDisplay(savedPrices.providerPriceCents);
        program.priceFloorCents = savedPrices.priceFloorCents;
        program.priceFloorDisplay = this.formatCentsToDisplay(savedPrices.priceFloorCents);
        program.ceilingPriceCents = savedPrices.ceilingPriceCents;
        program.ceilingPriceDisplay = this.formatCentsToDisplay(savedPrices.ceilingPriceCents);

        return true;
      });
    },

    buildUiRowsFromProgramsData() {
      const tempUiRows = {};

      this.programsData.forEach((program) => {
        if (!program.programGroup) return;

        if (!tempUiRows[program.name]) {
          tempUiRows[program.name] = {
            name: program.name,
            providerPriceCents: program.providerPriceCents,
            providerPriceDisplay: program.providerPriceDisplay,
            priceFloorCents: program.priceFloorCents,
            priceFloorDisplay: program.priceFloorDisplay,
            ceilingPriceCents: program.ceilingPriceCents,
            ceilingPriceDisplay: program.ceilingPriceDisplay,
            programGroupInputs: [],
            pricesByFormattedGroup: {},
          };
        }

        const formattedGroupName = this.formatProgramGroupName(program.programGroup);
        tempUiRows[program.name].pricesByFormattedGroup[formattedGroupName] = program.businessPlanPriceDisplay;

        tempUiRows[program.name].programGroupInputs.push(program);
      });

      this.uiRows = Object.values(tempUiRows)
        .filter((row) => row.programGroupInputs.length > 0)
        .map((row) => {
          row.programGroupInputs = orderBy(row.programGroupInputs, ["programGroup"]);
          return row;
        });
    },

    syncPricesToProgramsData() {
      this.uiRows.forEach((uiRow) => {
        const sharedProviderPrice = uiRow.providerPriceCents;
        const sharedPriceFloor = uiRow.priceFloorCents;
        const sharedCeilingPrice = uiRow.ceilingPriceCents;

        uiRow.programGroupInputs.forEach((pgInput) => {
          const programInProgramsData = this.programsData.find((p) => p.businessProgramId === pgInput.businessProgramId);

          if (programInProgramsData) {
            programInProgramsData.providerPriceCents = sharedProviderPrice;
            programInProgramsData.priceFloorCents = sharedPriceFloor;
            programInProgramsData.ceilingPriceCents = sharedCeilingPrice;

            programInProgramsData.businessPlanPriceCents = pgInput.businessPlanPriceCents;
          }
        });
      });
    },
    formatProgramGroupName(groupName) {
      if (!groupName) {
        return "";
      }
      let formattedName = groupName;
      const prefixMatch = groupName.match(/^(\d+\s*-\s*|)(\s*)(.*)$/);
      if (prefixMatch && prefixMatch[3]) {
        formattedName = prefixMatch[3];
      }

      if (formattedName.endsWith(" Pricing")) {
        formattedName = formattedName.replace(/\Pricing$/, "");
      }

      return formattedName;
    },

    async downloadPdf() {
      this.isDownloading = true; // Set loading state to true
      try {
        const element = document.getElementById("vendor-price-list-pdf-content");

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollY: -window.scrollY, // fix for scrolling issues
        });

        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        const pageHeight = pdf.internal.pageSize.getHeight();
        let position = 0;

        if (pdfHeight < pageHeight) {
          // Single page
          pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        } else {
          // Multi-page
          let heightLeft = pdfHeight;

          while (heightLeft > 0) {
            pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
            heightLeft -= pageHeight;
            position -= pageHeight;

            if (heightLeft > 0) {
              pdf.addPage();
            }
          }
        }

        pdf.save("Vendor_Price_List.pdf");
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "PDF Download Failed",
          message: "There was an issue generating the PDF. Please try again.",
          error,
        });
      } finally {
        this.isDownloading = false; // Set loading state to false after download or error
      }
    },
  },
};
</script>
